import { useEffect } from "react";
import { useForm, Form } from "../useForm";

import Input from "../../controls/Input";
import SelectMenu from "../../controls/SelectMenu";
import { SnackbarData, SnackbarType } from "../../Snackbar";
import { generateUniqueNumber } from "../../../shared/utility";

const inputClassName =
  "mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-35-custom px-3 focus:outline-none focus:ring-sky-500 focus:border-sky-500 sm:text-sm";

const initialFValues = {
  name: "",
  address: "",
  product: null,
  campaign_user: null,
};

export default function CampaignDetailsForm(props) {
  const {
    campaign,
    actionCall,
    loading,
    campaignUsersForDropdown,
    addSnackbar,
    launchCampaign,
    askDeleteCampaign,
    navigate,
  } = props;

  const { values, setValues, handleInputChange } = useForm(
    initialFValues,
    false,
  );

  useEffect(() => {
    if (campaign) {
      setValues({
        name: campaign.name ?? "",
        address: campaign.address ?? "",
        product: campaign.product ?? null,
        campaign_user: campaign.campaign_user ?? null,
      });
    }
  }, [campaign, campaignUsersForDropdown, setValues]);

  const handleSubmit = (e) => {
    e.preventDefault();
    actionCall(campaign, values);
  };

  const handleEmailCampaignInfo = () => {
    const productName = campaign?.product?.name_en ?? values.product?.name_en ?? "-";
    const body = [
      "Hi,",
      "",
      "Please find below the campaign information for your reference:",
      "",
      `Campaign Store: ${values.name || "-"}`,
      `Address: ${values.address || "-"}`,
      `Employee Name: ${values.campaign_user?.label ?? "-"}`,
      `PIN: ${values.campaign_user?.pin ?? "-"}`,
      `Product: ${productName}`,
      "",
      "Best regards,",
    ].join("\n");
    const subject = `Campaign Information - ${values.name || "Campaign"}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(mailtoUrl, "_blank");
    if (addSnackbar) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "Opening email client",
          SnackbarType.Success,
        ),
      );
    }
  };

  if (!campaign) {
    return null;
  }

  return (
    <Form
      classes="bg-white shadow-lg rounded-xl overflow-hidden"
      onSubmit={handleSubmit}
    >
      {/* Form Content */}
      <div className="px-6 py-8">
        {/* Store & Location Section */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
            Store & Location
          </h4>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <Input
                label="Store Name"
                name="name"
                onChange={handleInputChange}
                value={values.name}
                labelOn={true}
                type="text"
                className={inputClassName}
              />
            </div>
            <div>
              <Input
                label="Address"
                name="address"
                onChange={handleInputChange}
                value={values.address}
                labelOn={true}
                type="text"
                className={inputClassName}
                required={true}
              />
            </div>
          </div>
        </div>

        {/* Campaign Details Section */}
        <div className="mb-8">
          <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4 pb-2 border-b border-gray-200">
            Campaign Details
          </h4>
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
            <div>
              <SelectMenu
                name="campaign_user"
                handleInputChange={handleInputChange}
                options={campaignUsersForDropdown ?? []}
                title="Employee"
                value={values.campaign_user}
                placeholder="Select employee"
              />
              {values.campaign_user && (
                <p className="mt-1 text-xs text-gray-500">
                  PIN: {values.campaign_user.pin ?? "-"}
                </p>
              )}
            </div>
            <div>
              <Input
                label="Product"
                name="product"
                onChange={handleInputChange}
                value={values.product?.name_en ?? ""}
                labelOn={true}
                type="text"
                className={inputClassName}
                disabled={true}
              />
              <p className="mt-1 text-xs text-gray-500">
                Product cannot be changed after campaign creation
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-wrap items-center justify-end gap-3 pt-6 border-t border-gray-200">
          <button
            type="button"
            className="inline-flex min-w-[7rem] items-center justify-center rounded-md bg-orange-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-orange-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-orange-600"
            onClick={async () => {
              if (campaign.active === 0 && launchCampaign) {
                const result = await launchCampaign(campaign.id);
                if (result?.success && navigate) {
                  navigate(`/campaigns/${campaign.id}/view`);
                }
              } else if (navigate) {
                navigate(`/campaigns/${campaign.id}/view`);
              }
            }}
          >
            Launch
          </button>
          <button
            type="button"
            className="inline-flex min-w-[7rem] items-center justify-center rounded-md bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            onClick={handleEmailCampaignInfo}
          >
            Email info
          </button>
          <button
            type="submit"
            disabled={loading}
            className="inline-flex min-w-[7rem] items-center justify-center rounded-md bg-main-purple px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:opacity-50"
          >
            Save
          </button>
          <button
            type="button"
            className="inline-flex min-w-[7rem] items-center justify-center rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            onClick={() => askDeleteCampaign?.(campaign)}
          >
            Delete
          </button>
        </div>
      </div>
    </Form>
  );
}
